import { Link as RouterLink } from 'react-router-dom'

import { Avatar, Box, Button } from '@factorialco/deprecated-design-system'
import { usePerformanceGoalsPeopleDataTable } from 'generated/resources/datatable.graphql'

import DataTable from 'modules/core/components/DataTable'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { ProgressSummary } from 'modules/performance_goals/components/ProgressSummary'

const Actions: React.FC<{ id: number; name: string }> = ({ id, name }) => {
  const { navigation } = useNavigation()

  return (
    <RouterLink to={navigation.performanceGoals.employees.employee.fullPath({ id: id.toString() })}>
      <Button
        type="secondary"
        aria-label={i18n.t('performance_goals.employee_list.navigate', { name })}
        iconName="arrow-right"
      />
    </RouterLink>
  )
}

export const TeamTable: React.FC<{
  onlyDirectReports?: boolean
  search?: string
  cycleIds?: string[]
  noCycleSelected?: boolean
}> = ({ cycleIds, noCycleSelected, search, onlyDirectReports }) => {
  const source = usePerformanceGoalsPeopleDataTable(
    [
      {
        identifier: 'fullName',
        title: i18n.t('performance_goals.employee_list.columns.name'),
        render: ({ fullName, employee_access_avatar }) => (
          <Box gap="s8" flexDirection="row" alignItems="center">
            <Avatar url={buildUrl(employee_access_avatar)} label={fullName} size="s32" />
            {fullName}
          </Box>
        ),
        kind: 'custom',
        sortBy: 'fullName',
        template: '{{ fullName }}',
      },
      {
        identifier: 'manager',
        kind: 'path',
        title: 'Manager',
        path: 'employee_manager_fullName',
      },
      {
        identifier: 'metrics',
        kind: 'custom',
        title: i18n.t('performance_goals.employee_list.columns.progress'),
        render: ({ metricSummary, progresses }) => {
          const cycleProgress = progresses[0]

          if (cycleProgress) {
            return (
              <ProgressSummary
                progress={cycleProgress.progress}
                needsAttention={cycleProgress.needsAttention}
              />
            )
          }

          if (metricSummary) {
            return (
              <ProgressSummary
                progress={metricSummary.averageProgress}
                needsAttention={metricSummary.needsAttention}
              />
            )
          }
        },
        template: '{{ metricSummary_averageProgress }}',
      },
      {
        identifier: 'actions',
        kind: 'decorative',
        width: 80,
        fixed: true,
        render: ({ id, fullName }) => {
          return <Actions id={id} name={fullName} />
        },
      },
    ],
    {
      variables: {
        onlyDirectReports,
        search,
        cycleIds,
        noCycle: noCycleSelected,
      },
    }
  )

  return (
    <DataTable
      source={source}
      exportFileName={i18n.t('performance_goals.employee_list.export_filename')}
      disableEditColumns
      disableExport
      persistFilters={false}
      sort={{ path: 'fullName', direction: 'asc' }}
    />
  )
}
